// Objets d'une scène JRAF v1. Liste FERMÉE : un type inconnu est refusé.
// Toutes les coordonnées sont dans le repère du canevas (360 × 640 par défaut,
// origine en haut à gauche) et acceptent un nombre ou une expression.

import { z } from 'zod';
import { JRAF_LIMITS as L } from '../limits';
import {
  colorSchema,
  displayAffixSchema,
  displayTextSchema,
  jrafIdSchema,
  numExprSchema,
  pointSchema,
  styleSchema,
  textBoxSchema,
  valueExprSchema,
} from './primitives';

const initialSchema = z
  .object({
    visible: z.boolean().default(true),
    opacity: z.number().min(0).max(1).optional(),
  })
  .strict();

/**
 * Noms de l'objet dans la narration (« AB », « hypoténuse », « l'égalité de
 * Pythagore ») : quand la voix le nomme, le lecteur l'éclaire (lecteur 3).
 * Texte simple, sans formule : `AB` est trouvé dans « $AB^2$ ».
 */
export const mentionsSchema = z
  .array(
    z
      .string()
      .trim()
      .min(1)
      .max(L.maxMentionChars)
      .regex(/^[^$\\{}]+$/, 'un nom simple, sans formule ($, \\, accolades)'),
  )
  .min(1)
  .max(L.maxMentions);

/** Champs communs à tous les objets. */
const objectBase = {
  id: jrafIdSchema,
  /** groupe parent (ses décalages s'ajoutent) */
  parent: jrafIdSchema.optional(),
  initial: initialSchema.optional(),
  mentions: mentionsSchema.optional(),
};

export const rectObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('rect'),
    x: numExprSchema,
    y: numExprSchema,
    width: numExprSchema,
    height: numExprSchema,
    rx: numExprSchema.optional(),
    style: styleSchema.optional(),
  })
  .strict();

export const circleObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('circle'),
    cx: numExprSchema,
    cy: numExprSchema,
    r: numExprSchema,
    style: styleSchema.optional(),
  })
  .strict();

export const ellipseObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('ellipse'),
    cx: numExprSchema,
    cy: numExprSchema,
    rx: numExprSchema,
    ry: numExprSchema,
    style: styleSchema.optional(),
  })
  .strict();

export const lineObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('line'),
    x1: numExprSchema,
    y1: numExprSchema,
    x2: numExprSchema,
    y2: numExprSchema,
    style: styleSchema.optional(),
  })
  .strict();

export const arrowObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('arrow'),
    x1: numExprSchema,
    y1: numExprSchema,
    x2: numExprSchema,
    y2: numExprSchema,
    head: z.enum(['end', 'both']).default('end'),
    style: styleSchema.optional(),
  })
  .strict();

export const polygonObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('polygon'),
    points: z.array(pointSchema).min(3).max(L.maxPolygonPoints),
    style: styleSchema.optional(),
  })
  .strict();

export const pathObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('path'),
    /** grammaire restreinte, vérifiée à la validation (`isSafePathData`) */
    d: z.string().trim().min(1).max(L.maxPathChars),
    style: styleSchema.optional(),
  })
  .strict();

export const LABEL_POSITIONS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'] as const;

export const pointObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('point'),
    x: numExprSchema,
    y: numExprSchema,
    label: displayTextSchema(L.maxPointLabelChars).optional(),
    labelPosition: z.enum(LABEL_POSITIONS).default('ne'),
    style: styleSchema.optional(),
  })
  .strict();

export const TEXT_ALIGNS = ['start', 'middle', 'end'] as const;

export const textObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('text'),
    at: pointSchema,
    text: displayTextSchema(L.maxTextChars),
    size: z.number().min(10).max(64).default(18),
    align: z.enum(TEXT_ALIGNS).default('middle'),
    weight: z.enum(['normal', 'bold']).default('normal'),
    color: colorSchema.optional(),
    /** largeur max avant retour à la ligne (repère du canevas) */
    maxWidth: z.number().min(20).max(L.maxCoordinate).optional(),
    /** cadre autour du texte rendu (format 2 du lecteur) */
    box: textBoxSchema.optional(),
  })
  .strict();

export const formulaObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('formula'),
    at: pointSchema,
    /** LaTeX sans `$` ; rendu KaTeX `trust: false` */
    tex: z.string().trim().min(1).max(L.maxTexChars),
    size: z.number().min(10).max(72).default(26),
    align: z.enum(TEXT_ALIGNS).default('middle'),
    color: colorSchema.optional(),
    box: textBoxSchema.optional(),
  })
  .strict();

export const valueObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('value'),
    at: pointSchema,
    /** nombre affiché (écriture française) ; animé par `value.set` */
    expr: valueExprSchema,
    decimals: z.number().int().min(0).max(6).default(0),
    prefix: displayAffixSchema(20).optional(),
    suffix: displayAffixSchema(20).optional(),
    size: z.number().min(10).max(72).default(22),
    align: z.enum(TEXT_ALIGNS).default('middle'),
    color: colorSchema.optional(),
    box: textBoxSchema.optional(),
  })
  .strict();

export const imageObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('image'),
    asset: jrafIdSchema,
    x: numExprSchema,
    y: numExprSchema,
    width: numExprSchema,
    height: numExprSchema,
    alt: z.string().trim().min(1).max(200),
  })
  .strict();

export const groupObjectSchema = z
  .object({
    ...objectBase,
    type: z.literal('group'),
    /** décalage appliqué à ses enfants */
    x: numExprSchema.default(0),
    y: numExprSchema.default(0),
  })
  .strict();

export const jrafObjectSchema = z.discriminatedUnion('type', [
  rectObjectSchema,
  circleObjectSchema,
  ellipseObjectSchema,
  lineObjectSchema,
  arrowObjectSchema,
  polygonObjectSchema,
  pathObjectSchema,
  pointObjectSchema,
  textObjectSchema,
  formulaObjectSchema,
  valueObjectSchema,
  imageObjectSchema,
  groupObjectSchema,
]);
export type JrafObject = z.infer<typeof jrafObjectSchema>;
export type JrafObjectType = JrafObject['type'];

export const JRAF_OBJECT_TYPES = [
  'rect',
  'circle',
  'ellipse',
  'line',
  'arrow',
  'polygon',
  'path',
  'point',
  'text',
  'formula',
  'value',
  'image',
  'group',
] as const satisfies readonly JrafObjectType[];

/** Objets dont on peut « dessiner » le trait (`object.show` effet `draw`). */
/** Objets dessinés en HTML (texte mesuré par le navigateur), qui acceptent un cadre `box`. */
export const TEXT_LIKE_TYPES = [
  'text',
  'formula',
  'value',
] as const satisfies readonly JrafObjectType[];

export const DRAWABLE_TYPES: readonly JrafObjectType[] = [
  'rect',
  'circle',
  'ellipse',
  'line',
  'arrow',
  'polygon',
  'path',
];
